import { Suspense } from 'react';
import _ from 'lodash';
import {
    Icon,
    SuspenseBoundary,
    DeductionDetailsHeader,
    DeductionInfo,
    LoaderOverlay,
    DisputeButton,
    FileList
} from 'components';
import { 
    useDeductionDetails, 
    useDeductionDocuments, 
    useRefreshDeductionDetails,
} from 'state';
import {
    PaymentDetail,
    DeductionDetailHeaderData,
    DeductionInfoData,
} from 'types';
import { faMoneyBillTransfer } from '@fortawesome/pro-regular-svg-icons'
import { isPaidDeduction } from 'functions';
import { useBackupDocument, useDeductionParams } from 'hooks';
import DocumentErrorDialog from 'hooks/DocumentErrorDialog';
import styles from './DeductionDetailsPage.module.scss';

export default function DeductionDetailsPage() {
    const { accountId, vendorId, invoiceId, referenceId, voucherId, adjustmentIdentityKey } = useDeductionParams();
    const refresh = useRefreshDeductionDetails(accountId, vendorId, invoiceId, referenceId, voucherId, adjustmentIdentityKey);

    return (
        <SuspenseBoundary
            errorMessage={`Error retrieving deduction details ${invoiceId} ${referenceId}`}
            onReset={refresh}
        >
            <DeductionDetailsComponent />
        </SuspenseBoundary>
    );
}

const DeductionDetailsComponent = () => {
    const { accountId, vendorId, invoiceId, referenceId, voucherId, adjustmentIdentityKey } = useDeductionParams();
    const paymentDetails = useDeductionDetails(accountId, vendorId, invoiceId, referenceId, voucherId, adjustmentIdentityKey);
    const {
        paymentId = '',
        adjustmentAmount: deductionAmount = 0,
        customerId = '',
        customerName = '',
        invoiceDate,
        mockInvoice = '',
        invoiceId: deductionNumber = '',
        customerItemReference = '',
    } = paymentDetails;
    
    const getHeaderData = (): DeductionDetailHeaderData => {
        return {
            paymentIdRef: paymentId,
            deductionNumber: deductionNumber,
            deductionAmount: deductionAmount,
            customerId: customerId,
            customerName: customerName,
            vendorId: vendorId,
            vendorName: '',
        };
    }
    const deductionDetailHeader: DeductionDetailHeaderData = getHeaderData();

    const getDeductionInfo = (): DeductionInfoData => {
        return {
            invoiceDate: invoiceDate,
            deductionAmount: deductionAmount,
            mockInvoice: mockInvoice,
            deductionNumber: deductionNumber,
            referenceNumber: customerItemReference,
        }
    };
    const deductionInfo: DeductionInfoData = getDeductionInfo();

    return (
        <div className="details-page">
            <div className='details-page-title'>
                <Icon icon={faMoneyBillTransfer}/> 
                Deduction Details
            </div>
            <div>
                <div className={styles.buttonContainer}>
                    <DisputeButton 
                        selectedDetails={[paymentDetails]} 
                        context="deductionDetails"
                        disabled={!isPaidDeduction(paymentDetails.statusDescription)}
                    />
                </div>
            </div>
            <div className="details-page-main">
                <div className={styles.header}>
                    <DeductionDetailsHeader deductionDetailHeader={deductionDetailHeader}/>
                </div>
                <div className={styles.mainContent}>
                    <div className={styles.deductionInfoContainer}>
                        <DeductionInfo deductionInfo={deductionInfo}/>
                    </div>
                    <div className={styles.backupDocumentationContainer}>
                        <div className={styles.backupDocumentationTitle}>Backup Documentation</div>
                        <div className={styles.backupDocumentationFiles}>
                            <Suspense fallback={<LoaderOverlay isLoading />}>
                                {<Documents detail={paymentDetails} />}
                            </Suspense>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

type DetailProps = {
    detail: PaymentDetail
};

const Documents = ({ detail }: DetailProps) => {
    const documents = useDeductionDocuments(detail);
    const getDocument = useBackupDocument();
    
    return _.isEmpty(documents)
        ? <span className={styles.none}>No documents found</span>
        : <>
            <FileList documents={documents} onClick={getDocument} />
            {getDocument.showError && <DocumentErrorDialog onClose={() => getDocument.setShowError(false)} documentIdentifier={getDocument.errorIdentifier} />}
          </>;
};
